// src/features/dashboard/components/EmployeeLoggedHoursReport/MonthlyHoursTrend.tsx

import {
  useEffect,
  useRef,
  useState,
} from 'react';

import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  LabelList,
} from 'recharts';

import {
  Logged30DayResponse,
} from '../../types/Employee/employeeLoggedHours.types';

/* ============================================================================
 * PROPS
 * ========================================================================== */

interface Props {
  data?: Logged30DayResponse | null;
}

/* ============================================================================
 * TYPES
 * ========================================================================== */

interface ChartDataItem {
  day: string;
  value: number;
  formatted: string;
}

interface CustomLabelProps {
  x?: number;
  y?: number;
  value?: number | string;
}

interface TooltipPayloadItem {
  payload?: ChartDataItem;
}

interface CustomTooltipProps {
  active?: boolean;
  payload?: TooltipPayloadItem[];
}

interface CustomXAxisTickProps {
  x?: number;
  y?: number;

  payload?: {
    value?: string;
    index?: number;
  };

  activeIndex?: number | null;

  /*
   * Width of the complete SVG chart.
   * Used to keep the active box completely inside
   * the chart area.
   */
  chartWidth?: number;
}

/* ============================================================================
 * CHART CONFIGURATION
 * ========================================================================== */

const CHART_HEIGHT = 285;

const Y_AXIS_WIDTH = 46;

const TOP_MARGIN = 30;

const BOTTOM_MARGIN = 50;

const LEFT_MARGIN = 0;

const RIGHT_MARGIN = 30;

const X_AXIS_LEFT_PADDING = 35;

const X_AXIS_RIGHT_PADDING = 35;

const PLOT_HEIGHT =
  CHART_HEIGHT -
  TOP_MARGIN -
  BOTTOM_MARGIN;

const Y_AXIS_STEP = 4;

/* ============================================================================
 * ACTIVE X-AXIS BOX
 * ========================================================================== */

const ACTIVE_BOX_WIDTH = 50;

const ACTIVE_BOX_HEIGHT = 36;

const ACTIVE_ARROW_WIDTH = 8;

const ACTIVE_ARROW_HEIGHT = 8;

const ACTIVE_BOX_STROKE =
  '#98A2B3';

/* ============================================================================
 * CUSTOM X AXIS
 * ========================================================================== */

const CustomXAxisTick = ({
  x,
  y,
  payload,
  activeIndex,
  chartWidth,
}: CustomXAxisTickProps) => {
  if (
    x === undefined ||
    y === undefined ||
    !payload
  ) {
    return null;
  }

  const index =
    payload.index ?? 0;

  const label =
    payload.value ?? '';

  const isActive =
    activeIndex === index;

  /* ========================================================================
     NORMAL X-AXIS LABEL
  ======================================================================== */

  if (!isActive) {
    return (
      <text
        x={x}
        y={y}
        dy={12}
        textAnchor={
          index === 0
            ? 'start'
            : 'middle'
        }
        fill="#667085"
        fontSize={10}
      >
        {label}
      </text>
    );
  }

  /* ========================================================================
     ACTIVE DATE BOX

     IMPORTANT:
     We draw the complete tab as ONE path.

     This prevents the top border from being removed by
     the previous white masking rectangle.
  ======================================================================== */

  const halfWidth =
    ACTIVE_BOX_WIDTH / 2;

  /*
   * Calculate safe center.

   * The first point cannot move farther left than half the
   * box width.

   * The last point cannot move farther right than half the
   * box width.
   */

  const safeCenterX =
    chartWidth !== undefined
      ? Math.min(
          Math.max(
            halfWidth + 1,
            x,
          ),
          chartWidth -
            halfWidth -
            1,
        )
      : Math.max(
          halfWidth + 1,
          x,
        );

  /* ========================================================================
     BOX POSITION
  ======================================================================== */

  const boxLeft =
    safeCenterX -
    halfWidth;

  const boxRight =
    safeCenterX +
    halfWidth;

  const boxTop =
    y;

  const boxBottom =
    y +
    ACTIVE_BOX_HEIGHT;

  const arrowTop =
    y -
    ACTIVE_ARROW_HEIGHT;

  /* ========================================================================
     COMPLETE TAB PATH

     Shape:

              /\
             /  \
    ┌───────/    \───────┐
    │                    │
    │       Mon 24       │
    │                    │
    └────────────────────┘
  ======================================================================== */

  const activeBoxPath = `
    M ${boxLeft} ${boxTop}

    L ${
      safeCenterX -
      ACTIVE_ARROW_WIDTH
    } ${boxTop}

    L ${safeCenterX} ${arrowTop}

    L ${
      safeCenterX +
      ACTIVE_ARROW_WIDTH
    } ${boxTop}

    L ${boxRight} ${boxTop}

    L ${boxRight} ${boxBottom}

    L ${boxLeft} ${boxBottom}

    Z
  `;

  return (
    <g
      pointerEvents="none"
    >
      {/* ================================================================
          COMPLETE ACTIVE DATE BOX

          One single path means all borders remain visible.
      ================================================================ */}

      <path
        d={activeBoxPath}
        fill="#FFFFFF"
        stroke={
          ACTIVE_BOX_STROKE
        }
        strokeWidth={1}
        strokeLinejoin="round"
      />

      {/* ================================================================
          DATE TEXT
      ================================================================ */}

      <text
        x={safeCenterX}
        y={y + 24}
        textAnchor="middle"
        fill="#344054"
        fontSize={10}
      >
        {label}
      </text>
    </g>
  );
};

/* ============================================================================
 * CUSTOM VALUE LABEL
 * ========================================================================== */

const CustomLabel = ({
  x,
  y,
  value,
}: CustomLabelProps) => {
  if (
    x === undefined ||
    y === undefined ||
    value === undefined
  ) {
    return null;
  }

  const numericX =
    Number(x);

  const numericY =
    Number(y);

  const numericValue =
    Number(value);

  if (
    !Number.isFinite(
      numericX,
    ) ||
    !Number.isFinite(
      numericY,
    ) ||
    !Number.isFinite(
      numericValue,
    )
  ) {
    return null;
  }

  /*
   * Keep value label inside visible chart area.
   */

  const safeX =
    Math.max(
      20,
      numericX,
    );

  const safeY =
    Math.max(
      TOP_MARGIN + 28,
      numericY,
    );

  return (
    <g
      pointerEvents="none"
      aria-hidden="true"
    >
      <rect
        x={
          safeX - 18
        }
        y={
          safeY - 25
        }
        width={36}
        height={17}
        rx={4}
        ry={4}
        fill="#244E78"
        stroke="#244E78"
        strokeWidth={1}
      />

      <text
        x={safeX}
        y={safeY - 13}
        textAnchor="middle"
        dominantBaseline="middle"
        fill="#FFFFFF"
        fontSize={9}
        fontWeight={600}
      >
        {numericValue.toFixed(1)}h
      </text>
    </g>
  );
};

/* ============================================================================
 * CUSTOM TOOLTIP
 * ========================================================================== */

const CustomTooltip = ({
  active,
  payload,
}: CustomTooltipProps) => {
  if (
    !active ||
    !payload ||
    payload.length === 0
  ) {
    return null;
  }

  const item =
    payload[0]?.payload;

  if (!item) {
    return null;
  }

  return (
    <div
      className="
        min-w-[135px]
        rounded-lg
        border
        border-[#E5E7EB]
        bg-white
        px-3
        py-2
        shadow-lg
      "
    >
      <div
        className="
          mb-1
          text-[11px]
          font-medium
          text-[#667085]
        "
      >
        {item.day}
      </div>

      <div
        className="
          flex
          items-center
          gap-2
        "
      >
        <span
          className="
            h-2.5
            w-2.5
            shrink-0
            rounded-full
            bg-[#244E78]
          "
        />

        <span
          className="
            text-[12px]
            text-[#344054]
          "
        >
          Hours
        </span>

        <span
          className="
            ml-auto
            text-[12px]
            font-semibold
            text-[#101828]
          "
        >
          {item.formatted}
        </span>
      </div>
    </div>
  );
};

/* ============================================================================
 * COMPONENT
 * ========================================================================== */

const MonthlyHoursTrend = ({
  data,
}: Props) => {
  const scrollRef =
    useRef<HTMLDivElement | null>(
      null,
    );

  const [
    activeXAxisIndex,
    setActiveXAxisIndex,
  ] = useState<number | null>(
    null,
  );

  /* ==========================================================================
   * CHART DATA
   * ========================================================================== */

  const chartData: ChartDataItem[] =
    Array.isArray(data?.trend)
      ? data.trend.map(
          (item: any) => {
            let day = '';

            /* ----------------------------------------------------------------
               CREATE X-AXIS LABEL
            ---------------------------------------------------------------- */

            if (item?.workDate) {
              const date =
                new Date(
                  item.workDate,
                );

              if (
                !Number.isNaN(
                  date.getTime(),
                )
              ) {
                const dayName =
                  date.toLocaleDateString(
                    'en-US',
                    {
                      weekday:
                        'short',
                    },
                  );

                const dateNumber =
                  String(
                    date.getDate(),
                  ).padStart(
                    2,
                    '0',
                  );

                day =
                  `${dayName} ${dateNumber}`;
              }
            }

            /* ----------------------------------------------------------------
               FALLBACK
            ---------------------------------------------------------------- */

            if (
              !day &&
              item?.dayNumber !==
                undefined &&
              item?.dayNumber !==
                null
            ) {
              day =
                String(
                  item.dayNumber,
                );
            }

            /* ----------------------------------------------------------------
               LOGGED HOURS
            ---------------------------------------------------------------- */

            const loggedHours =
              Number(
                item?.loggedHours ??
                  0,
              );

            const safeValue =
              Number.isFinite(
                loggedHours,
              )
                ? loggedHours
                : 0;

            const formatted =
              `${safeValue.toFixed(
                1,
              )}h`;

            return {
              day,
              value:
                safeValue,
              formatted,
            };
          },
        )
      : [];

  /* ==========================================================================
   * RESET WHEN DATA CHANGES
   * ========================================================================== */

  useEffect(() => {
    if (
      scrollRef.current
    ) {
      scrollRef.current.scrollLeft = 0;
    }

    setActiveXAxisIndex(
      null,
    );
  }, [data]);

  /* ==========================================================================
   * Y AXIS
   * ========================================================================== */

  const yMin = 0;

  const yMax = 24;

  /* ==========================================================================
   * Y AXIS TICKS
   * ========================================================================== */

  const yTicks: number[] =
    [];

  for (
    let value = 0;
    value <= yMax;
    value +=
      Y_AXIS_STEP
  ) {
    yTicks.push(value);
  }

  /* ==========================================================================
   * Y POSITION
   * ========================================================================== */

  const getYAxisTop = (
    value: number,
  ) => {
    if (
      yMax <= yMin
    ) {
      return (
        TOP_MARGIN +
        PLOT_HEIGHT
      );
    }

    const ratio =
      (value - yMin) /
      (yMax - yMin);

    return (
      TOP_MARGIN +
      PLOT_HEIGHT -
      PLOT_HEIGHT *
        ratio
    );
  };

  const zeroAxisTop =
    getYAxisTop(0);

  /* ==========================================================================
   * GRAPH WIDTH
   * ========================================================================== */

  const pointWidth =
    chartData.length <= 7
      ? 72
      : chartData.length <= 14
        ? 66
        : 62;

  const minimumChartWidth =
    chartData.length <= 7
      ? 470
      : 580;

  const chartWidth =
    Math.max(
      minimumChartWidth,
      chartData.length *
        pointWidth +
        X_AXIS_LEFT_PADDING +
        X_AXIS_RIGHT_PADDING,
    );

  /* ==========================================================================
   * HORIZONTAL SCROLL
   * ========================================================================== */

  useEffect(() => {
    const container =
      scrollRef.current;

    if (!container) {
      return;
    }

    const handleNativeWheel = (
      event: WheelEvent,
    ) => {
      const canScroll =
        container.scrollWidth >
        container.clientWidth;

      if (!canScroll) {
        return;
      }

      if (
        Math.abs(
          event.deltaY,
        ) >
        Math.abs(
          event.deltaX,
        )
      ) {
        event.preventDefault();

        container.scrollLeft +=
          event.deltaY;
      }
    };

    container.addEventListener(
      'wheel',
      handleNativeWheel,
      {
        passive: false,
      },
    );

    return () => {
      container.removeEventListener(
        'wheel',
        handleNativeWheel,
      );
    };
  }, []);

  /* ==========================================================================
   * EMPTY STATE
   * ========================================================================== */

  if (
    !chartData.length
  ) {
    return (
      <div
        className="
          w-full
          min-w-0
          overflow-hidden
          rounded-[18px]
          border
          border-[#D0D5DD]
          bg-white
          p-4
        "
      >
        <div
          className="
            mb-3
            flex
            flex-wrap
            items-center
            gap-2
          "
        >
          <h3
            className="
              text-[16px]
              font-semibold
              text-[#002B5B]
            "
          >
            30-Day Hours Trend
          </h3>

          <span
            className="
              rounded-full
              bg-[#EEF4FF]
              px-3
              py-[4px]
              text-[10px]
              text-[#2563EB]
            "
          >
            Daily logged hours ·{' '}
            {data?.monthInfo
              ?.monthRange ??
              '--'}
          </span>
        </div>

        <div
          className="
            flex
            h-[285px]
            items-center
            justify-center
            text-[13px]
            text-[#667085]
          "
        >
          No logged hours data
          available
        </div>
      </div>
    );
  }

  /* ==========================================================================
   * RENDER
   * ========================================================================== */

  return (
    <div
      className="
        w-full
        min-w-0
        overflow-hidden
        rounded-[18px]
        border
        border-[#D0D5DD]
        bg-white
        p-4
      "
    >
      {/* ====================================================================
          HEADER
      ==================================================================== */}

      <div
        className="
          mb-3
          flex
          flex-wrap
          items-center
          gap-2
        "
      >
        <h3
          className="
            shrink-0
            text-[16px]
            font-semibold
            text-[#002B5B]
          "
        >
          30-Day Hours Trend
        </h3>

        <span
          className="
            shrink-0
            rounded-full
            bg-[#EEF4FF]
            px-3
            py-[4px]
            text-[10px]
            text-[#2563EB]
          "
        >
          Daily logged hours ·{' '}
          {data?.monthInfo
            ?.monthRange ??
            '--'}
        </span>
      </div>

      {/* ====================================================================
          CHART
      ==================================================================== */}

      <div
        className="
          flex
          w-full
          min-w-0
          overflow-hidden
        "
      >
        {/* ==================================================================
            FIXED Y AXIS
        ================================================================== */}

        <div
          className="
            relative
            z-30
            shrink-0
            bg-white
          "
          style={{
            width:
              Y_AXIS_WIDTH,
            minWidth:
              Y_AXIS_WIDTH,
            height:
              CHART_HEIGHT,
          }}
        >
          {/* Y AXIS LINE */}

          <div
            className="
              absolute
              right-0
              w-px
              bg-[#D0D5DD]
            "
            style={{
              top:
                TOP_MARGIN,
              height:
                zeroAxisTop -
                TOP_MARGIN,
            }}
          />

          {/* Y AXIS LABELS */}

          {yTicks.map(
            (tick) => {
              const top =
                getYAxisTop(
                  tick,
                );

              return (
                <span
                  key={tick}
                  className="
                    absolute
                    right-[7px]
                    -translate-y-1/2
                    whitespace-nowrap
                    bg-white
                    pr-[1px]
                    text-[10px]
                    leading-none
                    text-[#667085]
                  "
                  style={{
                    top,
                  }}
                >
                  {tick}h
                </span>
              );
            },
          )}
        </div>

        {/* ==================================================================
            GRAPH
        ================================================================== */}

        <div
          ref={scrollRef}
          className="
            monthly-hours-scroll
            relative
            z-10
            min-w-0
            flex-1
            overflow-x-auto
            overflow-y-hidden
            overscroll-x-contain
            overscroll-y-none
          "
          style={{
            scrollbarWidth:
              'none',
            msOverflowStyle:
              'none',
          }}
        >
          <div
            className="
              relative
              overflow-visible
            "
            style={{
              width:
                `${chartWidth}px`,
              minWidth:
                `${chartWidth}px`,
              height:
                CHART_HEIGHT,
            }}
          >
            <ResponsiveContainer
              width="100%"
              height={
                CHART_HEIGHT
              }
            >
              <LineChart
                data={
                  chartData
                }
                margin={{
                  top:
                    TOP_MARGIN,
                  right:
                    RIGHT_MARGIN,
                  left:
                    LEFT_MARGIN,
                  bottom:
                    BOTTOM_MARGIN,
                }}
                onMouseMove={(
                  state: any,
                ) => {
                  const index =
                    state?.activeTooltipIndex;

                  if (
                    index !==
                      undefined &&
                    index !==
                      null &&
                    Number.isFinite(
                      Number(
                        index,
                      ),
                    )
                  ) {
                    setActiveXAxisIndex(
                      Number(
                        index,
                      ),
                    );
                  }
                }}
                onMouseLeave={() => {
                  setActiveXAxisIndex(
                    null,
                  );
                }}
              >
                {/* ========================================================
                    GRID
                ======================================================== */}

                <CartesianGrid
                  vertical={false}
                  stroke="#ECEFF3"
                  strokeWidth={1}
                />

                {/* ========================================================
                    Y AXIS
                ======================================================== */}

                <YAxis
                  yAxisId="left"
                  domain={[
                    yMin,
                    yMax,
                  ]}
                  ticks={
                    yTicks
                  }
                  hide
                />

                {/* ========================================================
                    X AXIS
                ======================================================== */}

                <XAxis
                  dataKey="day"
                  type="category"
                  scale="point"
                  interval={0}
                  axisLine={{
                    stroke:
                      '#D0D5DD',
                    strokeWidth: 1,
                  }}
                  tickLine={false}
                  tick={(
                    props: any,
                  ) => (
                    <CustomXAxisTick
                      x={props.x}
                      y={props.y}
                      payload={
                        props.payload
                      }
                      activeIndex={
                        activeXAxisIndex
                      }
                      chartWidth={
                        chartWidth
                      }
                    />
                  )}
                  height={1}
                  tickMargin={8}
                  padding={{
                    left:
                      X_AXIS_LEFT_PADDING,
                    right:
                      X_AXIS_RIGHT_PADDING,
                  }}
                />

                {/* ========================================================
                    TOOLTIP
                ======================================================== */}

                <Tooltip
                  cursor={{
                    stroke:
                      '#D0D5DD',
                    strokeDasharray:
                      '4 4',
                  }}
                  wrapperStyle={{
                    zIndex: 100,
                  }}
                  content={
                    <CustomTooltip />
                  }
                />

                {/* ========================================================
                    LINE
                ======================================================== */}

                <Line
                  yAxisId="left"
                  type="monotone"
                  dataKey="value"
                  stroke="#244E78"
                  strokeWidth={2}
                  connectNulls
                  isAnimationActive={
                    false
                  }
                  animationDuration={
                    0
                  }
                  dot={{
                    r: 3,
                    fill:
                      '#244E78',
                    stroke:
                      '#FFFFFF',
                    strokeWidth: 1,
                  }}
                  activeDot={{
                    r: 5,
                    fill:
                      '#244E78',
                  }}
                >
                  <LabelList
                    dataKey="value"
                    position="top"
                    offset={10}
                    content={(
                      props: any,
                    ) => (
                      <CustomLabel
                        x={
                          props.x !==
                          undefined
                            ? Number(
                                props.x,
                              )
                            : undefined
                        }
                        y={
                          props.y !==
                          undefined
                            ? Number(
                                props.y,
                              )
                            : undefined
                        }
                        value={
                          props.value
                        }
                      />
                    )}
                  />
                </Line>
              </LineChart>
            </ResponsiveContainer>

            {/* ==============================================================
                EXACT 0h LINE
            ============================================================== */}

            <div
              className="
                pointer-events-none
                absolute
                left-0
                right-0
                h-px
                bg-[#D0D5DD]
              "
              style={{
                top:
                  zeroAxisTop,
              }}
            />
          </div>
        </div>
      </div>

      {/* ====================================================================
          HIDE SCROLLBAR
      ==================================================================== */}

      <style>
        {`
          .monthly-hours-scroll::-webkit-scrollbar {
            display: none;
            width: 0;
            height: 0;
          }

          .monthly-hours-scroll {
            scrollbar-width: none;
            -ms-overflow-style: none;
          }
        `}
      </style>
    </div>
  );
};

export default MonthlyHoursTrend;